import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import BlockIcon from '@mui/icons-material/Block'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import MergeIcon from '@mui/icons-material/CallMerge'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import type {
  ContactDetail,
  ContactIdentityDetail,
  MergeHistoryItem,
} from '../../api/contacts'

export interface ContactDetailFormValues {
  name: string | null
  location: string | null
  notes: string | null
}

export interface ContactDetailProps {
  contact: ContactDetail
  identities: ContactIdentityDetail[]
  mergeHistory: MergeHistoryItem[]
  isAdmin: boolean
  saving: boolean
  blocking: boolean
  deleting: boolean
  onSave: (values: ContactDetailFormValues) => void
  onToggleBlockContact: (blocked: boolean) => void
  onMerge: () => void
  onDelete: () => void
  onIdentityToggleBlock: (identityId: number, blocked: boolean) => void
  onIdentityReassign: (identityId: number) => void
  deleteDisabledReason?: string
}

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return 'Never'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString()
}

const sanitizeDisplay = (value: string | null): string => (value && value.trim().length > 0 ? value : '—')

const isHeaderPreviewUseful = (
  headerPreview?: ContactIdentityDetail['header_preview'],
): headerPreview is NonNullable<ContactIdentityDetail['header_preview']> => {
  if (!headerPreview) {
    return false
  }
  const messageId = headerPreview.message_id?.trim() ?? ''
  if (messageId.length > 0) {
    return true
  }
  const referencesCount = headerPreview.references_count ?? null
  return typeof referencesCount === 'number' && referencesCount > 0
}

const ContactDetail: React.FC<ContactDetailProps> = ({
  contact,
  identities,
  mergeHistory,
  isAdmin,
  saving,
  blocking,
  deleting,
  onSave,
  onToggleBlockContact,
  onMerge,
  onDelete,
  onIdentityToggleBlock,
  onIdentityReassign,
  deleteDisabledReason,
}) => {
  const [values, setValues] = React.useState<ContactDetailFormValues>({
    name: contact.name ?? '',
    location: contact.location ?? '',
    notes: contact.notes ?? '',
  })
  const [dirty, setDirty] = React.useState(false)
  const [headerPreviewExpanded, setHeaderPreviewExpanded] = React.useState<Record<number, boolean>>({})

  React.useEffect(() => {
    setValues({
      name: contact.name ?? '',
      location: contact.location ?? '',
      notes: contact.notes ?? '',
    })
    setDirty(false)
  }, [contact.id, contact.name, contact.location, contact.notes])

  const handleFieldChange = (field: keyof ContactDetailFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextValue = event.target.value
      setValues((prev) => ({ ...prev, [field]: nextValue }))
      setDirty(true)
    }

  const handleSave = () => {
    if (!dirty) {
      return
    }
    onSave({
      name: values.name?.trim() || null,
      location: values.location?.trim() || null,
      notes: values.notes?.trim() || null,
    })
  }

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!dirty) {
        return
      }
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault()
        handleSave()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [dirty, values])

  const toggleBlockLabel = contact.blocked ? 'Unblock contact' : 'Block contact'

  return (
    <Stack spacing={3} sx={{ py: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          label="Name"
          value={values.name}
          onChange={handleFieldChange('name')}
          fullWidth
        />
        <TextField
          label="Location"
          value={values.location}
          onChange={handleFieldChange('location')}
          fullWidth
        />
      </Stack>
      <TextField
        label="Notes"
        value={values.notes}
        onChange={handleFieldChange('notes')}
        multiline
        minRows={4}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!dirty || saving}
        >
          Save changes
        </Button>
        <Button
          variant={contact.blocked ? 'outlined' : 'contained'}
          color="secondary"
          startIcon={<BlockIcon />}
          onClick={() => onToggleBlockContact(!contact.blocked)}
          disabled={blocking}
        >
          {toggleBlockLabel}
        </Button>
        <Button startIcon={<MergeIcon />} onClick={onMerge} variant="outlined">
          Merge contact
        </Button>
        <Tooltip title={deleteDisabledReason ?? ''} disableHoverListener={!deleteDisabledReason}>
          <span>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={onDelete}
              disabled={deleting || !isAdmin}
            >
              Delete contact
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Divider />

      <Stack spacing={2}>
        <Typography variant="h6">Linked identities</Typography>
        {identities.length === 0 && (
          <Alert severity="info">This contact has no linked identities.</Alert>
        )}
        {identities.map((identity) => {
          const sourceLabel = identity.source_label?.trim() ?? ''
          const firstSeenDisplay = identity.first_seen_at
            ? formatDateTime(identity.first_seen_at)
            : null
          const lastSeenDisplay = identity.last_seen_at
            ? formatDateTime(identity.last_seen_at)
            : null
          const metaItems = [
            sourceLabel !== '' ? `Via: ${sourceLabel}` : null,
            firstSeenDisplay ? `First seen: ${firstSeenDisplay}` : null,
            lastSeenDisplay ? `Last seen: ${lastSeenDisplay}` : null,
          ].filter((item): item is string => Boolean(item))

          const headerPreviewUseful = isHeaderPreviewUseful(identity.header_preview)
          const isHeaderPreviewExpanded = headerPreviewExpanded[identity.id] ?? false
          const messageIdRaw = identity.header_preview?.message_id ?? null
          const sanitizedMessageId = sanitizeDisplay(messageIdRaw)
          const referencesCount = identity.header_preview?.references_count

          return (
            <Accordion key={identity.id} disableGutters variant="outlined" defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {identity.display_name || identity.external_id || identity.platform}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {identity.platform} — {sanitizeDisplay(identity.external_id)}
                    </Typography>
                    {metaItems.length > 0 && (
                      <Box
                        role="group"
                        aria-label="Identity meta"
                        sx={{
                          mt: 0.5,
                          display: 'flex',
                          flexWrap: 'wrap',
                          rowGap: 0.5,
                          columnGap: { xs: 0.75, sm: 1.5 },
                          typography: 'caption',
                          color: 'text.secondary',
                          '& > *': {
                            display: 'inline-flex',
                            alignItems: 'center',
                            minHeight: 20,
                          },
                          '& > :not(:first-of-type)::before': {
                            content: { xs: '""', sm: '"•"' },
                            color: 'text.disabled',
                            display: { xs: 'none', sm: 'inline-block' },
                            marginRight: { xs: 0, sm: 0.75 },
                            marginLeft: { xs: 0, sm: 0.75 },
                          },
                        }}
                      >
                        {metaItems.map((item) => (
                          <Box key={item} component="span" sx={{ wordBreak: 'break-word' }}>
                            {item}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Stack>
                  {identity.blocked && <Chip color="warning" label="Blocked" size="small" />}
                  <Chip label={`${identity.message_count} messages`} size="small" />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {headerPreviewUseful && (
                    <Stack spacing={0.5}>
                      <Button
                        variant="text"
                        size="small"
                        color="inherit"
                        onClick={() =>
                          setHeaderPreviewExpanded((prev) => ({
                            ...prev,
                            [identity.id]: !prev[identity.id],
                          }))
                        }
                        sx={{
                          alignSelf: 'flex-start',
                          px: 0,
                          textTransform: 'none',
                          typography: 'caption',
                        }}
                        aria-expanded={isHeaderPreviewExpanded}
                        aria-controls={`identity-${identity.id}-header-preview`}
                      >
                        Technical details
                      </Button>
                      {isHeaderPreviewExpanded && (
                        <Stack
                          id={`identity-${identity.id}-header-preview`}
                          spacing={0.25}
                          sx={{ typography: 'caption', color: 'text.secondary' }}
                        >
                          <Typography
                            component="div"
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              alignItems: 'baseline',
                            }}
                          >
                            <Box component="span">Message-ID:</Box>
                            <Box
                              component="span"
                              title={messageIdRaw?.trim() || undefined}
                              sx={{
                                flexGrow: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {sanitizedMessageId}
                            </Box>
                          </Typography>
                          {typeof referencesCount === 'number' && (
                            <Typography
                              component="div"
                              sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline' }}
                            >
                              <Box component="span">References:</Box>
                              <Box component="span">{referencesCount}</Box>
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  )}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      variant="outlined"
                      startIcon={identity.blocked ? <PersonOffIcon /> : <BlockIcon />}
                      onClick={() => onIdentityToggleBlock(identity.id, !identity.blocked)}
                    >
                      {identity.blocked ? 'Unblock identity' : 'Block identity'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<LinkOffIcon />}
                      onClick={() => onIdentityReassign(identity.id)}
                    >
                      Reassign identity
                    </Button>
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>

      {mergeHistory.length > 0 && (
        <Stack spacing={1}>
          <Divider />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Merge history
          </Typography>
          <Stack spacing={1.5}>
            {mergeHistory.map((entry) => (
              <Box key={`${entry.from_contact_id}-${entry.merged_at}`}>
                <Typography variant="body2" color="text.secondary">
                  Merged {entry.from_name || `Contact #${entry.from_contact_id}`} on {formatDateTime(entry.merged_at)} by{' '}
                  {entry.merged_by}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default ContactDetail
